/**
 * LiveKit session_settings helpers (join media, waiting room, focus / PiP).
 */

export type JoinMediaDefaults = {
  microphone: boolean;
  camera: boolean;
};

export type SessionJoinDefaults = {
  trainer: JoinMediaDefaults;
  student: JoinMediaDefaults;
};

export type LiveSessionSettings = {
  waiting_room_enabled: boolean;
  /** When true, tab switches count as violations; student PiP is blocked. */
  distraction_detection_enabled: boolean;
  /**
   * When distraction detection is off, students auto-open Document PiP
   * (no PiP button). Forced false while distraction detection is on.
   */
  student_pip_allowed: boolean;
  /** When false, students cannot unmute until trainer allows. */
  students_can_unmute: boolean;
  /** When false, students cannot turn camera on until trainer allows. */
  students_can_enable_camera: boolean;
  join_defaults: SessionJoinDefaults;
};

export function defaultSessionSettings(): LiveSessionSettings {
  return {
    waiting_room_enabled: false,
    distraction_detection_enabled: true,
    student_pip_allowed: true,
    students_can_unmute: false,
    students_can_enable_camera: false,
    join_defaults: {
      trainer: { microphone: false, camera: false },
      student: { microphone: false, camera: false },
    },
  };
}

function asBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return Boolean(value);
  if (typeof value === "string") {
    const t = value.trim().toLowerCase();
    if (["1", "true", "yes", "on"].includes(t)) return true;
    if (["0", "false", "no", "off"].includes(t)) return false;
  }
  return fallback;
}

export function normalizeSessionSettings(
  raw?: unknown,
): LiveSessionSettings {
  const base = defaultSessionSettings();
  if (!raw || typeof raw !== "object") return base;
  const obj = raw as Record<string, unknown>;
  const join =
    obj.join_defaults && typeof obj.join_defaults === "object"
      ? (obj.join_defaults as Record<string, unknown>)
      : {};

  const roleDefaults = (
    role: "trainer" | "student",
    fallback: JoinMediaDefaults,
  ): JoinMediaDefaults => {
    const roleObj =
      join[role] && typeof join[role] === "object"
        ? (join[role] as Record<string, unknown>)
        : {};
    return {
      microphone: asBool(
        roleObj.microphone ?? obj[`${role}_default_mic`],
        fallback.microphone,
      ),
      camera: asBool(
        roleObj.camera ?? obj[`${role}_default_camera`],
        fallback.camera,
      ),
    };
  };

  const distractionOn = asBool(
    obj.distraction_detection_enabled,
    base.distraction_detection_enabled,
  );
  const pipAllowed = asBool(
    obj.student_pip_allowed,
    base.student_pip_allowed,
  );

  return {
    waiting_room_enabled: asBool(
      obj.waiting_room_enabled,
      base.waiting_room_enabled,
    ),
    distraction_detection_enabled: distractionOn,
    student_pip_allowed: distractionOn ? false : pipAllowed,
    students_can_unmute: asBool(
      obj.students_can_unmute,
      base.students_can_unmute,
    ),
    students_can_enable_camera: asBool(
      obj.students_can_enable_camera,
      base.students_can_enable_camera,
    ),
    join_defaults: {
      trainer: roleDefaults("trainer", base.join_defaults.trainer),
      student: roleDefaults("student", base.join_defaults.student),
    },
  };
}

export function joinDefaultsForRole(
  settings: unknown,
  role: "trainer" | "student",
): JoinMediaDefaults {
  return normalizeSessionSettings(settings).join_defaults[role];
}

/** Students should auto-open PiP (no button) under this policy. */
export function shouldAutoOpenStudentPip(
  settings: LiveSessionSettings,
): boolean {
  return (
    !settings.distraction_detection_enabled && settings.student_pip_allowed
  );
}
